import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { auctionItems as mockAuctionItems } from "@/mocks/auctionItems";

/**
 * 전문가 PICK 물건 — kangstudy_auction_items 를 읽는다.
 *
 * 그 테이블은 두 종류의 행을 담는다:
 *  - source='auto'   : kangstudy_sync_auction_items() 가 EchoTwin auction_cases 에서
 *                      매일 06:30 KST 에 큐레이션해 넣는다(신건 제외·치명위험 0·
 *                      위험원천 확인 완료만).
 *  - source='manual' : 관리자가 엑셀로 직접 등록. 싱크가 건드리지 않는다.
 *
 * DB 는 snake_case, 화면은 camelCase 라 여기서 한 번만 옮긴다.
 *
 * ⚠️ 조회 실패 시 목업으로 폴백하되 `usingMock` 을 같이 돌려준다. 조용히
 * 폴백하면 테이블 이름이 바뀌거나 RLS 가 막혔을 때 "글이 좀 적네" 로만 보이고
 * 아무도 못 알아챈다(2026-08-18 개명 사고가 정확히 그랬다).
 */
export interface AuctionItemView {
  id: string;
  caseNumber: string;
  court: string;
  location: string;
  propertyType: string;
  appraisedValue: number;
  minimumPrice: number;
  auctionDate: string;
  failureCount: number;
  hasTenant: boolean;
  rightsAnalysis: string;
  /** 시세 추정이 없으면 null — 감정가를 시세로 대체하지 않는다. */
  expectedSalePrice: number | null;
  /** 시세 − 최저가×1.07(취득비용). 시세가 없으면 null. */
  expectedProfit: number | null;
  grade: string;
  exclusiveArea: number | null;
  buildingAge: number | null;
  landShare: string | null;
  floorInfo: string | null;
  imageUrl?: string;
}

function toView(r: Record<string, unknown>): AuctionItemView {
  const num = (v: unknown): number | null =>
    v === null || v === undefined || v === "" ? null : Number(v);
  return {
    id: String(r.id),
    caseNumber: (r.case_number as string) || "",
    court: (r.court as string) || "",
    location: (r.location as string) || "",
    propertyType: (r.property_type as string) || "",
    appraisedValue: num(r.appraised_value) ?? 0,
    minimumPrice: num(r.minimum_price) ?? 0,
    auctionDate: (r.auction_date as string) || "",
    failureCount: num(r.failure_count) ?? 0,
    hasTenant: Boolean(r.has_tenant),
    rightsAnalysis: (r.rights_analysis as string) || "",
    expectedSalePrice: num(r.expected_sale_price),
    expectedProfit: num(r.expected_profit),
    grade: (r.grade as string) || "C",
    exclusiveArea: num(r.exclusive_area),
    buildingAge: num(r.building_age),
    landShare: (r.land_share as string) || null,
    floorInfo: (r.floor_info as string) || null,
  };
}

/** 목업(camelCase, 일부 필드 없음)을 같은 뷰 모양으로 맞춘다. */
function mockToView(m: (typeof mockAuctionItems)[number]): AuctionItemView {
  return {
    id: m.id,
    caseNumber: m.caseNumber,
    court: m.court,
    location: m.location,
    propertyType: m.propertyType,
    appraisedValue: m.appraisedValue,
    minimumPrice: m.minimumPrice,
    auctionDate: m.auctionDate,
    failureCount: m.failureCount,
    hasTenant: m.hasTenant,
    rightsAnalysis: m.rightsAnalysis,
    expectedSalePrice: m.expectedSalePrice ?? null,
    expectedProfit: m.expectedProfit ?? null,
    grade: m.grade,
    exclusiveArea: m.exclusiveArea ?? null,
    buildingAge: m.buildingAge ?? null,
    landShare: null,
    floorInfo: null,
    imageUrl: m.imageUrl,
  };
}

export function useAuctionItems() {
  const [items, setItems] = useState<AuctionItemView[]>([]);
  const [loading, setLoading] = useState(true);
  const [usingMock, setUsingMock] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("kangstudy_auction_items")
        .select("*")
        .order("auction_date", { ascending: true });

      if (error) throw error;
      if (data && data.length > 0) {
        setItems(data.map((r) => toView(r as Record<string, unknown>)));
        setUsingMock(false);
      } else {
        setItems(mockAuctionItems.map(mockToView));
        setUsingMock(true);
      }
    } catch (e) {
      console.error("[kangstudy] 전문가 PICK 조회 실패 — 목업으로 폴백합니다:", e);
      setItems(mockAuctionItems.map(mockToView));
      setUsingMock(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return { items, loading, usingMock, refetch: fetchItems };
}
